// Portal "Meetings" tab (kanban 108573ba).
//
// Owner on review 311c384e (2026-10-08): "make sure that access to these is
// scoped based on who the user is and we have to make it a tab that shows
// up. There's a My Roadmap thing already and that kind of helps with this."
//
// So: the same two-pane agenda the leadership /roadmap Meeting tab shows,
// for the meetings THIS person is in. The backend (GET /my-roadmap/meetings)
// decides which series they may open and hands over only the initiatives
// and questions those agendas are built from; leadership gets every series.
// Here there is no initiative page to open and no transcript viewer (both
// live on the leadership-only /roadmap), and the reader may answer only the
// questions Merlin addresses to them; the room's other questions are read.
// "See all N initiatives" under the head goes to My roadmap, the portal's
// list of every initiative (kanban c3097c0a).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { myRoadmapApi, type MyMeetingsResponse } from '../../services/myRoadmap';
import type { ClarifyingQuestion, QuestionIdentity, QuestionResponseRequest } from '../../services/roadmapReports';
import { MeetingPanel } from '../roadmap/MeetingPanel';
import { defaultSeries, type SeriesId } from '../roadmap/meetingSeries';
import { assignTeamColors, questionAddressees } from '../roadmap/roadmapViewUtils';
import styles from '../roadmap/RoadmapV2.module.css';

export default function MyMeetingsPage() {
  const [data, setData] = useState<MyMeetingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Bumped after an answer; the effect re-reads. State is set only from the
  // fetch's callbacks (never synchronously in the effect body).
  const [version, setVersion] = useState(0);
  const [pickedSeries, setPickedSeries] = useState<SeriesId | null>(null);
  const [todayIso] = useState(() => new Date().toISOString().slice(0, 10));
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    myRoadmapApi
      .meetings()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load your meetings — try again in a moment.');
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const respond = useCallback(async (req: QuestionResponseRequest) => {
    await myRoadmapApi.respond(req);
    setVersion((v) => v + 1);
  }, []);
  const withdraw = useCallback(async (id: QuestionIdentity) => {
    await myRoadmapApi.withdraw(id);
    setVersion((v) => v + 1);
  }, []);

  const me = useMemo(() => new Set((data?.me ?? []).map((n) => n.toLowerCase())), [data]);
  const canRespondTo = useCallback(
    (q: ClarifyingQuestion) => questionAddressees(q).some((a) => me.has(a.toLowerCase())),
    [me],
  );
  const teamColors = useMemo(() => {
    const teams = new Map<string, string>();
    for (const i of data?.initiatives ?? []) for (const t of i.teams) if (!teams.has(t.toLowerCase())) teams.set(t.toLowerCase(), t);
    return assignTeamColors([...teams.values()].sort((a, b) => a.localeCompare(b)));
  }, [data]);

  if (error) return <div className={styles.page}><p className={styles.respondError} role="alert">{error}</p></div>;
  if (!data) return <div className={styles.page}><p className={styles.muted}>Loading…</p></div>;

  const series = pickedSeries ?? defaultSeries(data.series, new Date())?.id ?? '';

  return (
    <div className={styles.page} data-testid="my-meetings">
      <header className={styles.myHead}>
        <h1 className={styles.myTitle}>Meetings</h1>
        <p className={styles.muted}>
          {data.series.length === 0
            ? 'The agenda for each meeting you are in, once the roadmap lists you.'
            : `The agenda for ${data.series.length === 1 ? 'your meeting' : 'each of your meetings'}: what to decide, what is stuck, and the words behind each item. Up and Down move through it on a shared screen; Space marks an item covered.`}
        </p>
      </header>
      <MeetingPanel
        initiatives={data.initiatives}
        questions={data.questions}
        today={todayIso}
        seriesList={data.series}
        series={series}
        onSeriesChange={setPickedSeries}
        teamColors={teamColors}
        onRespond={respond}
        onWithdraw={withdraw}
        canRespondTo={canRespondTo}
        onOpenList={() => navigate('/portal/my-roadmap')}
        asOf={data.report_date}
      />
    </div>
  );
}
